// src/components/expediente/ModalEditarPaciente.js
import React, { useState } from 'react';
import api from '../../services/api';
import { conVersion } from '../../utils/concurrencia';
import { Modal } from '../common/ui';
import { fechaSegura } from '../../utils/formatos';

const inputCls = "w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm font-medium focus:outline-none focus:border-clinica-primary focus:ring-2 focus:ring-clinica-primary/15 transition-all";
const labelCls = "block mb-1.5 text-[11px] font-black text-slate-500 uppercase tracking-wide";

// Campo de solo lectura (dato de identidad inalterable por ley)
function CampoBloqueado({ label, valor }) {
    return (
        <div>
            <span className={labelCls}>{label}</span>
            <div className="flex items-center justify-between gap-2 py-2.5 px-4 bg-slate-100 border border-slate-200 rounded-xl">
                <span className="text-sm font-bold text-slate-500 truncate">{valor || '—'}</span>
                <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
            </div>
        </div>
    );
}

/**
 * Edición de los DATOS ADMINISTRATIVOS del paciente (derecho de rectificación RGPD).
 * La identidad que ancla la historia clínica (DNI y fecha de nacimiento) se muestra
 * pero queda BLOQUEADA — el backend también la descarta por seguridad (Ley 41/2002).
 */
export default function ModalEditarPaciente({ open, onClose, paciente, onSuccess, mostrarToast }) {
    const [form, setForm] = useState(() => ({
        nombre: paciente?.nombre || '',
        apellidos: paciente?.apellidos || '',
        telefono: paciente?.telefono || '',
        email: paciente?.email || '',
        direccion: paciente?.direccion || '',
        ciudad: paciente?.ciudad || '',
        codigoPostal: paciente?.codigoPostal || '',
        seguroMedico: paciente?.seguroMedico || 'ninguno',
        contactoEmergenciaNombre: paciente?.contactoEmergenciaNombre || '',
        contactoEmergenciaTelefono: paciente?.contactoEmergenciaTelefono || ''
    }));
    const [guardando, setGuardando] = useState(false);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const guardar = async () => {
        if (!form.nombre.trim() || !form.apellidos.trim()) {
            mostrarToast?.('Nombre y apellidos son obligatorios.', 'error');
            return;
        }
        try {
            setGuardando(true);
            await api.put(`/pacientes/${paciente.id || paciente._id}`, form, conVersion(paciente));
            mostrarToast?.('Datos del paciente actualizados.', 'success');
            onSuccess?.();
            onClose?.();
        } catch (err) {
            mostrarToast?.(err.message || 'No se pudieron guardar los cambios.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            titulo="Editar datos del paciente"
            subtitulo="Rectificación de datos administrativos (RGPD)"
            size="lg"
        >
            <div className="space-y-5">
                {/* Aviso legal */}
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <p className="text-xs font-semibold text-amber-800">
                        Puedes corregir los datos personales y de contacto. El <strong>DNI/NIE y la fecha de nacimiento</strong> están bloqueados: anclan la historia clínica y son inalterables por ley. El historial clínico y los documentos firmados tampoco se modifican.
                    </p>
                </div>

                {/* Identidad bloqueada */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <CampoBloqueado label="DNI / NIE (bloqueado)" valor={paciente?.dni} />
                    <CampoBloqueado label="Fecha de nacimiento (bloqueada)" valor={fechaSegura(paciente?.fechaNacimiento, 'dd/MM/yyyy')} />
                </div>

                <hr className="border-slate-100" />

                {/* Datos editables */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="ep-nombre" className={labelCls}>Nombre *</label>
                        <input id="ep-nombre" className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-apellidos" className={labelCls}>Apellidos *</label>
                        <input id="ep-apellidos" className={inputCls} value={form.apellidos} onChange={e => set('apellidos', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-tel" className={labelCls}>Teléfono</label>
                        <input id="ep-tel" className={inputCls} value={form.telefono} onChange={e => set('telefono', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-email" className={labelCls}>Email</label>
                        <input id="ep-email" type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="ep-dir" className={labelCls}>Dirección</label>
                        <input id="ep-dir" className={inputCls} value={form.direccion} onChange={e => set('direccion', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-ciudad" className={labelCls}>Ciudad</label>
                        <input id="ep-ciudad" className={inputCls} value={form.ciudad} onChange={e => set('ciudad', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-cp" className={labelCls}>Código postal</label>
                        <input id="ep-cp" className={inputCls} value={form.codigoPostal} onChange={e => set('codigoPostal', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-seguro" className={labelCls}>Seguro médico</label>
                        <select id="ep-seguro" className={inputCls} value={form.seguroMedico} onChange={e => set('seguroMedico', e.target.value)}>
                            <option value="ninguno">Ninguno / Privado</option>
                            <option value="publico">Público</option>
                            <option value="privado">Privado</option>
                            <option value="mutua">Mutua</option>
                        </select>
                    </div>
                    <div />
                    <div>
                        <label htmlFor="ep-emer-nom" className={labelCls}>Contacto de emergencia</label>
                        <input id="ep-emer-nom" className={inputCls} placeholder="Nombre" value={form.contactoEmergenciaNombre} onChange={e => set('contactoEmergenciaNombre', e.target.value)} />
                    </div>
                    <div>
                        <label htmlFor="ep-emer-tel" className={labelCls}>Teléfono de emergencia</label>
                        <input id="ep-emer-tel" className={inputCls} placeholder="Teléfono" value={form.contactoEmergenciaTelefono} onChange={e => set('contactoEmergenciaTelefono', e.target.value)} />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                    <button onClick={onClose} disabled={guardando} className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">
                        Cancelar
                    </button>
                    <button onClick={guardar} disabled={guardando} className="px-5 py-2.5 text-sm font-bold text-white bg-clinica-primary hover:bg-blue-700 rounded-xl transition-colors shadow-md min-w-[140px] disabled:opacity-60">
                        {guardando ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
